
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AudioLines, Play, TriangleAlert, Upload } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardBody,
  CardHead,
  Field,
  SearchableSelect,
  Select,
  Textarea,
} from '../../components/ui';
import { useToolTask, useTtsRun, useTtsRuns } from '../../hooks';
import type { AudioTimingsResult, PlacementProposalRequest } from '../../types/api';
import { isTaskFinished } from '../../types/api';
import { ContextField } from './ContextField';
import { DensityStep } from './DensityStep';
import { EMPTY_DENSITY, densityFormOf, densityPayload, type DensityForm } from './density';
import { ProposalResult } from './ProposalResult';
import { RunProgress } from './RunProgress';
import {
  LANGUAGES,
  MAX_TEXT_CHARS,
  contextPayload,
  countWords,
  fileSize,
  mmss,
} from './format';
import { parseProposal, type PlacementProposalView } from './types';
import { useAudioTimings, useCreateProposal, useProposal, validatePlacementAudio } from './usePlacement';
import { useProposalView } from './useProposalView';
import styles from './placement.module.css';

export function AudioMode({ canRun }: { canRun: boolean }) {
  const [params, setParams] = useSearchParams();
  const alignTaskId = params.get('align') ?? undefined;
  const taskId = params.get('task') ?? undefined;
  const proposalIdParam = params.get('proposal') ?? undefined;

  const [file, setFile] = useState<File | null>(null);
  const [script, setScript] = useState('');
  const [context, setContext] = useState('');
  const [language, setLanguage] = useState('ru');
  const [density, setDensity] = useState<DensityForm>(EMPTY_DENSITY);
  const [showForm, setShowForm] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [ttsRunId, setTtsRunId] = useState('');

  const runs = useTtsRuns({ limit: 50 });
  const ttsRun = useTtsRun(ttsRunId || undefined);

  const align = useAudioTimings();
  const alignTask = useToolTask(alignTaskId);
  const create = useCreateProposal();
  const task = useToolTask(taskId);

  const timings = useMemo<AudioTimingsResult | null>(
    () =>
      alignTask.data?.status === 'succeeded'
        ? (alignTask.data.result as unknown as AudioTimingsResult)
        : null,
    [alignTask.data],
  );

  const taskProposal = useMemo(
    () => (task.data?.status === 'succeeded' ? parseProposal(task.data.result) : null),
    [task.data],
  );

  const startedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!timings || !alignTaskId || taskId) return;
    if (startedFor.current === alignTaskId) return;
    startedFor.current = alignTaskId;

    const payload: PlacementProposalRequest = {
      segments: [timings.text],
      context: contextPayload(context),
      language,
      density: densityPayload(density),
      alternatives: Number(density.alternatives) || 0,
      allow_media_reuse: density.allowMediaReuse,
      word_maps: timings.word_maps,
    };
    create.mutate(payload, {
      onSuccess: (started) => {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('task', started.id);
            return next;
          },
          { replace: true },
        );
      },
    });
  }, [alignTaskId, context, create, density, language, setParams, taskId, timings]);

  const syncedId = useRef<string | null>(null);
  useEffect(() => {
    if (!taskProposal || syncedId.current === taskProposal.proposalId) return;
    syncedId.current = taskProposal.proposalId;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('proposal', taskProposal.proposalId);
        return next;
      },
      { replace: true },
    );
  }, [taskProposal, setParams]);

  const remote = useProposal(taskProposal ? undefined : proposalIdParam);
  const proposal: PlacementProposalView | null = taskProposal ?? remote.data ?? null;
  const view = useProposalView(proposal);

  const aligning = Boolean(alignTaskId) && !isTaskFinished(alignTask.data?.status) && !alignTask.isError;
  const placing = Boolean(taskId) && !isTaskFinished(task.data?.status) && !task.isError;
  const busy = aligning || placing || align.start.isPending || create.isPending;

  const problems = file ? validatePlacementAudio(file) : [];
  const scriptTooLong = script.length > MAX_TEXT_CHARS;
  const contextTooLong = context.length > MAX_TEXT_CHARS;
  const hasSource = Boolean(file) || Boolean(ttsRunId);
  const canStart = canRun && hasSource && problems.length === 0 && !scriptTooLong && !contextTooLong && !busy;

  const startRun = useCallback(() => {
    // Прогон нашей озвучки уже несёт пословные тайминги: выравнивать нечего,
    // сразу подбираем вставки.
    if (ttsRunId && ttsRun.data) {
      const runData = ttsRun.data;
      create.mutate(
        {
          // Текста сегмента бэк не отдаёт, но word-map это он и есть: те же слова
          // в том же порядке, по ним и строятся якоря.
          segments: runData.segments.map((segment) =>
            segment.words.map((word) => word.word).join(' '),
          ),
          context: contextPayload(context),
          language,
          density: densityPayload(density),
          alternatives: Number(density.alternatives) || 0,
          allow_media_reuse: density.allowMediaReuse,
          word_maps: runData.segments.map((segment) => ({
            index: segment.index,
            words: segment.words,
            duration_ms: segment.duration_ms,
            timing_source: segment.timing_source,
          })),
        },
        {
          onSuccess: (started) => {
            syncedId.current = null;
            view.reset();
            setShowForm(false);
            setParams({ mode: 'audio', task: started.id }, { replace: false });
          },
        },
      );
      return;
    }
    if (!file) return;
    align.start.mutate(
      { file, script, language },
      {
        onSuccess: (started) => {
          startedFor.current = null;
          syncedId.current = null;
          view.reset();
          setShowForm(false);
          // mode=audio обязан выжить: setParams заменяет адрес целиком, и без него
          // экран уезжает на «Есть текст», а оплаченное выравнивание в никуда
          setParams({ mode: 'audio', align: started.id }, { replace: false });
        },
      },
    );
  }, [
    align.start,
    context,
    create,
    density,
    file,
    language,
    script,
    setParams,
    ttsRun.data,
    ttsRunId,
    view,
  ]);

  const formVisible = !proposal || showForm || busy;

  return (
    <>
      {align.start.isError && <ErrorBanner error={align.start.error} />}
      {create.isError && <ErrorBanner error={create.error} />}

      {alignTask.data?.status === 'failed' && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <TriangleAlert size={15} className={styles.bannerIconWarn} />
          <span className={styles.bannerText}>
            Тайминги не получились: {alignTask.data.error ?? 'причина неизвестна'}
          </span>
        </div>
      )}

      {timings && (
        <div className={`${styles.banner}`}>
          <AudioLines size={15} />
          <span className={styles.bannerText}>
            {timings.origin === 'forced_alignment'
              ? `Сверено с текстом, тайм-коды точные. Слов: ${timings.word_count}.`
              : `Распознано на слух, тайм-коды приблизительные. Слов: ${timings.word_count}.`}
          </span>
        </div>
      )}

      {formVisible && (
        <div className={styles.formGrid}>
          <div className={styles.formColumn}>
            <Card>
              <CardHead index="01" title="Озвучка и текст" />
              <CardBody pad className={styles.panelBody}>
                {runs.data && runs.data.items.length > 0 && (
                  <Field
                    layout="stacked"
                    label="Готовая озвучка из нашего инструмента"
                    hint="выберите прогон, и файл прикладывать не нужно"
                  >
                    {(id) => (
                      <Select
                        id={id}
                        value={ttsRunId}
                        disabled={busy || Boolean(file)}
                        onChange={(event) => setTtsRunId(event.target.value)}
                      >
                        <option value="">не выбрана</option>
                        {runs.data.items.map((item) => (
                          <option key={item.run_id} value={item.run_id}>
                            {item.title} · {mmss(item.total_duration_ms)}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                )}

                {ttsRunId ? null : file ? (
                  <div className={styles.fileRow}>
                    <span className={styles.fileName} title={file.name}>
                      {file.name}
                    </span>
                    <span className={styles.fileMeta}>{fileSize(file.size)}</span>
                    <Button size="sm" disabled={busy} onClick={() => setFile(null)}>
                      Другой файл
                    </Button>
                  </div>
                ) : (
                  <label
                    htmlFor="audio-source"
                    className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ''}`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragging(false);
                      if (!busy) setFile(event.dataTransfer.files?.[0] ?? null);
                    }}
                  >
                    <input
                      id="audio-source"
                      type="file"
                      className={styles.hiddenInput}
                      accept="audio/*,video/*"
                      disabled={busy}
                      onChange={(event) => {
                        const picked = event.target.files?.[0] ?? null;
                        event.target.value = '';
                        setFile(picked);
                      }}
                    />
                    <Upload size={22} strokeWidth={1.4} aria-hidden="true" />
                    <div className={styles.dropTitle}>
                      {align.progress !== null
                        ? 'Файл уходит на сервер'
                        : 'Перетащите озвучку или выберите'}
                    </div>
                    <div className={styles.dropMeta}>mp3 · wav · m4a · ogg · видео тоже подойдёт</div>
                    {align.progress !== null && (
                      <div className={styles.uploadBar}>
                        <span className={styles.progressTrack} aria-hidden="true">
                          <span
                            className={styles.progressFill}
                            style={{ width: `${Math.round(align.progress * 100)}%` }}
                          />
                        </span>
                        <span className={styles.progressPct}>
                          {Math.round(align.progress * 100)}%
                        </span>
                      </div>
                    )}
                  </label>
                )}

                {problems.length > 0 && <div className={styles.hint}>{problems.join('. ')}</div>}

                <ContextField value={context} onChange={setContext} disabled={busy} />

                <Field
                  layout="stacked"
                  label="Текст озвучки"
                  hint="Без него тайм-коды будут приблизительными."
                >
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={12}
                      value={script}
                      disabled={busy}
                      placeholder="Тот же текст, что звучит в файле"
                      onChange={(event) => setScript(event.target.value)}
                    />
                  )}
                </Field>
                <div className={styles.hint}>
                  <span>{countWords(script)} слов</span>
                  {scriptTooLong && (
                    <span className={styles.hint}>
                      Текст длиннее лимита в {MAX_TEXT_CHARS} символов
                    </span>
                  )}
                </div>

                <Field layout="stacked" label="Язык озвучки">
                  {() => (
                    <SearchableSelect
                      fullWidth
                      options={[...LANGUAGES]}
                      value={language}
                      onChange={(next) => setLanguage(next ?? language)}
                      disabled={busy}
                      ariaLabel="Язык озвучки"
                    />
                  )}
                </Field>
              </CardBody>
            </Card>
          </div>

          <div className={styles.formColumn}>
            <DensityStep
              index="02"
              value={density}
              onChange={setDensity}
              disabled={busy || !canRun}
              applied={proposal?.density ?? null}
              onUseApplied={() =>
                proposal && setDensity(densityFormOf(proposal.density, density))
              }
            />

            <Button variant="primary" disabled={!canStart} onClick={startRun}>
              <Play size={14} strokeWidth={1.8} />
              {busy ? 'Подбираем…' : 'Подобрать вставки'}
            </Button>
            {align.progress !== null && (
              <div className={styles.hint}>Отправка: {Math.round(align.progress * 100)}%</div>
            )}
          </div>
        </div>
      )}

      {aligning && <RunProgress task={alignTask.data} />}
      {placing && <RunProgress task={task.data} />}

      {proposal && !busy && (
        <ProposalResult
          proposal={proposal}
          feed={view.feed}
          mediaOf={view.mediaOf}
          overriddenOrders={view.overriddenOrders}
          selectedOrder={view.selectedOrder}
          onSelect={view.select}
          selectedItem={view.selectedItem}
          onChoose={view.choose}
          onResetChoice={view.resetChoice}
          onRerun={() => setShowForm(true)}
        />
      )}
    </>
  );
}
